import { useMemo, useSyncExternalStore } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { BrowserAuthError, BrowserAuthErrorCodes } from "@azure/msal-browser";
import { NavigateFunction } from "react-router-dom";
import { clearAllAccounts, identifyAccount, InitializedAccount } from "./init";
import { fetchAccessToken } from "./requests";
import {
  doLoginRedirect,
  doPostLoginRedirect,
  navigateToLogin,
  POST_LOGIN_REDIRECT,
} from "./redirect";
import { Auth } from "../useAuth";

function createStore<T>(initialValue: T) {
  let value = initialValue;
  const listeners = new Set<() => void>();

  const getSnapshot = () => value;

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const set = (newValue: T | ((prev: T) => T)) => {
    const next =
      typeof newValue === "function"
        ? (newValue as (prev: T) => T)(value)
        : newValue;
    if (Object.is(value, next)) return;
    value = next;
    for (const listener of listeners) {
      listener();
    }
  };

  const get = () => value;

  return { subscribe, getSnapshot, set, get } as const;
}

// --- Auth Store ---

type AuthStoreState = {
  initializedAccount: InitializedAccount;
  getToken: () => Promise<string>;
  refreshAccessToken: () => Promise<string>;

  /**
   * Correlation ID for the current session. Is _always_ used when getting a token.
   */
  correlationId?: string;
  logout: () => Promise<void> | void;

  /**
   * This is a cached token to be used for rendering UI.
   * Do not use this for API calls, as it may be stale. Instead, see `useGetToken`
   */
  renderingToken: string;
};

const authStore = createStore<AuthStoreState | undefined>(undefined);

/**
 * Subscribe to the full auth store state. Re-renders on any change.
 * This hook is for internal use and can return undefined if the store is not initialized yet.
 */
const useAuthStore = (): AuthStoreState | undefined =>
  useSyncExternalStore(authStore.subscribe, authStore.getSnapshot);

const getTokenInternal = async (skipCache = false): Promise<string> => {
  const store = authStore.getSnapshot();
  if (!store) {
    throw new Error(
      "Not authenticated. You should not be calling this outside of the auth guard.",
    );
  }

  let token: string;
  try {
    token = await fetchAccessToken(
      store.initializedAccount.authApp,
      store.initializedAccount.authSource,
      skipCache,
      store.correlationId,
    );
  } catch (error) {
    const isPopupBlocked =
      error instanceof BrowserAuthError &&
      (error.errorCode === BrowserAuthErrorCodes.popupWindowError ||
        error.errorCode === BrowserAuthErrorCodes.emptyWindowError);

    datadogRum.addError(error, {
      feature: "auth",
      stage: isPopupBlocked ? "acquireTokenPopup" : "fetchAccessToken",
    });
    await doLoginRedirect(
      store.initializedAccount.authApp,
      store.initializedAccount.authConfig,
      store.initializedAccount.account,
    );
    throw new Error("Failed to fetch token for authenticated account", {
      cause: error,
    });
  }

  if (store.renderingToken !== token) {
    datadogRum.addAction("Token refreshed", {
      feature: "auth",
    });
    authStore.set({ ...store, renderingToken: token });
  }
  return token;
};

const setCorrelationId = async (correlationId: string) => {
  authStore.set((prev) => {
    if (!prev)
      throw new Error(
        "Auth store should be initialized before setting correlation ID. Are you working inside the AuthGuard?",
      );
    return {
      ...prev,
      correlationId,
    };
  });
  await getTokenInternal(true); // skip cache to ensure the correlation ID is used
};

const unsetCorrelationId = async () => {
  authStore.set((prev) => {
    if (!prev)
      throw new Error(
        "Auth store should be initialized before unsetting correlation ID. Are you working inside the AuthGuard?",
      );
    return { ...prev, correlationId: undefined };
  });
  await getTokenInternal(true); // skip cache to ensure the correlation ID is used
};

let initPromise: Promise<void> | undefined;

/**
 * Try to initialize auth by identifying an account and trying to fetch a token.
 * If any of that fails, redirect to the login page.
 * Updates the auth store with the initialized account.
 * @param navigate - The navigate function from react-router, used to redirect to login if needed.
 */
export const initializeAuthSpaSSO = (
  navigate: NavigateFunction,
): Promise<void> => {
  if (initPromise) return initPromise;
  initPromise = initializeAuthInternal(navigate).finally(() => {
    // If auth failed to initialize (store is still undefined), allow retrying.
    if (authStore.getSnapshot() === undefined) {
      initPromise = undefined;
    }
  });
  return initPromise;
};

/**
 * This does the real initialization work.
 * We wrap this function to ensure it's only called once at a time.
 *
 * @see initializeAuthSpaSSO
 */
const initializeAuthInternal = async (navigate: NavigateFunction) => {
  // Auth is already initialized, no need to do anything
  if (authStore.getSnapshot() !== undefined) {
    return;
  }

  let initializedAccount;
  try {
    initializedAccount = await identifyAccount();
  } catch (error) {
    console.error(
      "Failed to identify account during auth initialization",
      error,
    );
    datadogRum.addError(error, {
      feature: "auth",
      stage: "identifyAccount",
    });
    navigateToLogin(navigate);
    return;
  }
  if (!initializedAccount) {
    navigateToLogin(navigate);
    return;
  }

  // We've successfully identified an account. Make sure we can get a token.
  let token: string;
  try {
    token = await fetchAccessToken(
      initializedAccount.authApp,
      initializedAccount.authSource,
    );
  } catch (error) {
    datadogRum.addError(error, {
      feature: "auth",
      stage: "fetchAccessToken",
    });
    navigateToLogin(navigate);
    return;
  }

  const logout = async () => {
    // Make sure the redirect path is empty. This is probably redundant.
    globalThis.window.sessionStorage.removeItem(POST_LOGIN_REDIRECT);
    const account = initializedAccount.authApp.getActiveAccount();
    // Clear the MSAL cache
    await clearAllAccounts();
    initializedAccount.authApp.setActiveAccount(null);

    // Redirect to the MSAL logout endpoint, which will clear the SSO session and then redirect back to our app.
    await initializedAccount.authApp.logoutRedirect({
      account,
    });
  };

  authStore.set({
    initializedAccount,
    getToken: () => getTokenInternal(false /* allow cache */),
    refreshAccessToken: () => getTokenInternal(true /* skip the cache */),
    logout,
    renderingToken: token,
  });

  doPostLoginRedirect(navigate);
};

/** Checks whether spaSSO is READY TO GO */
export const useIsInitializedSpaSSO = (): boolean => {
  const store = useAuthStore();
  return store !== undefined;
};

/**
 * Test-only helper to seed the spaSSO auth store so `useSpaSSO`/`useAuth`
 * return a valid `Auth` object without running the real `AuthGuard`.
 *
 * The default `renderingToken` is empty so `parseJwt` returns undefined.
 */
export const initializeSpaSSOForTesting = (renderingToken = ""): void => {
  authStore.set({
    // The initialized account is only read when fetching real tokens, which
    // never happens on the seeded test path.
    initializedAccount: undefined as unknown as InitializedAccount,
    getToken: () => Promise.resolve(renderingToken),
    refreshAccessToken: () => Promise.resolve(renderingToken),
    logout: () => Promise.resolve(),
    renderingToken,
  });
};

export const useSpaSSO = (): Auth | undefined => {
  const authStore = useAuthStore();

  return useMemo(() => {
    if (!authStore) return;
    return {
      ssoImplementation: "spaSSO" as const,
      getAccessToken: authStore.getToken,
      renderingToken: authStore.renderingToken,
      refreshAccessToken: authStore.refreshAccessToken,
      logout: authStore.logout,
      setCorrelationId,
      unsetCorrelationId,
    };
  }, [authStore]);
};
